import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { gqlClient } from '@/api/graphql-client';
import dashboardKeys from '@/api/queries/dashboard/keyFactory';
import { graphql } from '@/gql';
import { formatMonthLabel } from '@/utils/dashboard';

const GET_DASHBOARD_INVOICE_STATS = graphql(`
  query GetDashboardInvoiceStats($input: DashboardInvoiceStatsInput) {
    dashboardInvoiceStats(input: $input) {
      sent {
        count
        totalValue
        byDate {
          date
          value
        }
      }
      due {
        count
        totalValue
        byDate {
          date
          value
        }
      }
      paid {
        count
        totalValue
        byDate {
          date
          value
        }
      }
      timeSeries {
        month
        sentCount
        sentValue
        dueCount
        dueValue
        paidCount
        paidValue
      }
    }
  }
`);

export interface GetDashboardInvoiceStatsOptions {
  dateRange?: {
    from?: Date;
    to?: Date;
  };
}

const useGetDashboardInvoiceStats = (
  options?: GetDashboardInvoiceStatsOptions
) => {
  const { dateRange } = options ?? {};

  const { data, ...rest } = useQuery({
    queryKey: dashboardKeys.invoiceStats(
      dateRange
        ? { sentAtFrom: dateRange.from, sentAtTo: dateRange.to }
        : undefined
    ),
    queryFn: () =>
      gqlClient.request(GET_DASHBOARD_INVOICE_STATS, {
        input: {
          sentAtFrom: dateRange?.from?.toISOString(),
          sentAtTo: dateRange?.to?.toISOString(),
        },
      }),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Transform the data to match component expectations
  const stats = useMemo(() => {
    if (!data?.dashboardInvoiceStats) return null;

    const { sent, due, paid, timeSeries } = data.dashboardInvoiceStats;

    const transformCountAndValue = (
      item: typeof sent | typeof due | typeof paid
    ) => ({
      count: item.count,
      totalValue: { value: item.totalValue },
      sparkline: item.byDate.map((d) => d.value),
      sparklineValue: item.byDate.map((d) => d.value),
    });

    return {
      sent: transformCountAndValue(sent),
      due: transformCountAndValue(due),
      paid: transformCountAndValue(paid),
      timeSeries: timeSeries.map((item) => ({
        period: item.month,
        label: formatMonthLabel(item.month),
        sentCount: item.sentCount,
        sentValue: { value: item.sentValue },
        dueCount: item.dueCount,
        dueValue: { value: item.dueValue },
        paidCount: item.paidCount,
        paidValue: { value: item.paidValue },
      })),
    };
  }, [data]);

  return {
    data,
    stats,
    ...rest,
  };
};

export default useGetDashboardInvoiceStats;
